import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "defaultOpen", "icon", "isOpen", "onClose", "renderCloseButton", "size"];
import PropTypes from 'prop-types';
import React, { cloneElement, forwardRef, useCallback, useState } from 'react';
import { ICON_SIZES, TAG_APPEARANCES, TAG_SIZES } from '../../constants';
import { TagCloseButton } from '../TagCloseButton/TagCloseButton';
import { StyledTag } from './Tag.styles';

/**
 * "md" tag gets "xs" icon
 * "sm" tag gets no icon
 */
var TAG_ICON_SIZES = {
  md: ICON_SIZES.xs
};

/**
 * @since 8.6.0
 */
export var Tag = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var children = props.children,
    defaultOpen = props.defaultOpen,
    icon = props.icon,
    controlledOpen = props.isOpen,
    onClose = props.onClose,
    renderCloseButton = props.renderCloseButton,
    size = props.size,
    rest = _objectWithoutProperties(props, _excluded);
  var _useState = useState(defaultOpen),
    _useState2 = _slicedToArray(_useState, 2),
    uncontrolledOpen = _useState2[0],
    setUncontrolledOpen = _useState2[1];
  var isOpen = uncontrolledOpen;
  if (typeof controlledOpen !== 'undefined') {
    isOpen = controlledOpen;
  }
  var closeCallback = useCallback(function () {
    setUncontrolledOpen(false);
    if (typeof onClose === 'function') {
      onClose(props);
    }
  }, [onClose, props, setUncontrolledOpen]);
  if (typeof isOpen === 'boolean' && !isOpen) {
    return null;
  }
  var iconSize = TAG_ICON_SIZES[size];
  var adjustedIcon;
  if (icon && iconSize) {
    adjustedIcon = /*#__PURE__*/cloneElement(icon, {
      size: iconSize
    });
  }
  var closeButton;
  var appearance;
  if (isOpen && iconSize && typeof renderCloseButton === 'function') {
    closeButton = renderCloseButton({
      onClick: closeCallback
    });
    appearance = 'closeable';
  }
  var tag = /*#__PURE__*/React.createElement(StyledTag, _extends({
    ref: forwardedRef,
    size: size,
    appearance: appearance || 'gray'
  }, rest), adjustedIcon, children, closeButton);
  return tag;
});
Tag.displayName = 'Tag';
Tag.propTypes = {
  /**
   * Tags are static by default, but you can set this to true to enable the ability to close a
   * tag. When enabled, the tag will close in an uncontrolled manner. You can use the `isOpen`
   * prop to control the open state in a controlled manner.
   *
   * @since 8.25.0
   */
  defaultOpen: PropTypes.bool,
  /**
   * A closeable tag where the close state is controlled.
   *
   * @since 8.25.0
   */
  isOpen: PropTypes.bool,
  /**
   * Function called when the tag is closed. The tag's props will be passed as the only argument.
   *
   * @since 8.25.0
   */
  onClose: PropTypes.func,
  /**
   * Change the tag size.
   */
  size: PropTypes.oneOf(Object.values(TAG_SIZES)),
  /**
   * Change the tag text transform.
   *
   * @since 8.58.0
   */
  textTransform: PropTypes.oneOf(['capitalize', 'lowercase', 'uppercase', 'none']),
  /**
   * Add an `Icon` to the tag.
   *
   * Note: small tags do not support icons.
   */
  icon: PropTypes.node,
  /**
   * Change the tag colors. Defaults to `"gray"` for static tags, and `"closeable"` for closeable
   * tags.
   */
  appearance: PropTypes.oneOf(Object.values(TAG_APPEARANCES)),
  /**
   * Text content of tag.
   */
  children: PropTypes.node,
  /**
   * A render function that returns a close button.
   *
   * @since 8.28.0
   */
  renderCloseButton: PropTypes.func
};
Tag.defaultProps = {
  size: TAG_SIZES.md,
  textTransform: 'uppercase',
  icon: undefined,
  children: undefined,
  renderCloseButton: function renderCloseButton(props) {
    return /*#__PURE__*/React.createElement(TagCloseButton, props);
  }
};